/**
 * @fileoverview The frame of the public gift pages (/gift, /gift/thanks, /gift/redeem): the
 * Grow mark, a switch to the other language, and the page in a narrow column,
 * in the same look as the share pages. No account needed, so outside the
 * app's layout.
 *
 * A ?lang=es (or en) in the address opens the page in that language, as the
 * gift and receipt emails' links say (useGiftLanguage); the switch keeps the
 * choice on this device, as the sign-in page's does.
 *
 * Where: around GiftPage, GiftThanksPage and GiftRedeemPage.
 * API: none.
 * Words (en/es): auth.otherLanguage (the switch's "Español" / "English").
 *
 * @module GiftShell
 */

import React from 'react';
import { Gift } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { chooseGiftLanguage, useGiftLanguage } from '../lib/useGiftLanguage';

/**
 * The gift pages' frame.
 *
 * @param props.children - The page.
 */
export const GiftShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { reactTranslate: t } = useTranslation();
  const language = useGiftLanguage();
  /** Switches to the other language, kept on this device; the page is redrawn in it. */
  const switchLanguage = (): void => chooseGiftLanguage(language === 'es' ? 'en' : 'es');
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto w-full max-w-lg px-4 py-10">
        <div className="mb-8 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2 font-grow text-2xl font-bold text-primary">
            <Gift className="h-6 w-6" aria-hidden="true" />
            Grow
          </a>
          {/* In the other language: "Español" on the English page, "English" on the Spanish. */}
          <button
            type="button"
            onClick={switchLanguage}
            className="shrink-0 text-sm font-semibold text-primary underline-offset-4 hover:underline"
            lang={language === 'es' ? 'en' : 'es'}
          >
            {t('auth.otherLanguage')}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default GiftShell;
